import { FormInstance } from 'antd';
import type { SortOrder } from 'antd/lib/table/interface';
import { omit } from 'lodash';
import moment, { Moment } from 'moment';
import { useEffect, useState } from 'react';
import { useApiParserQuery } from './useApiParserQuery';
import { useMessage } from './useMessage';

export type BaseApiRequestParam = {
  current?: number;
  pageSize?: number;
  sort?: string;
  select?: string;
  filter?: string;
};

export type UseApiParserOption<V> = {
  // khai báo thêm các filter bổ sung
  additionalFilter?: any;
  // hàm xử lý để thêm các param khác vào api request
  additionRequestParams?: (
    filterQuery: Record<string, any>,
  ) => Record<string, any> | any | V;
  // danh sách các files sẽ sử dụng like thay vì so sánh equal
  fieldsUsingLike?: string[];
  // hàm transform giá trị Date thành date string
  formatDateWithTime?: (value: Moment) => string;
  // hàm gọi để validation request params trước khi call API
  validateRequestParams?: (
    filterQuery: Record<string, any>,
  ) => Promise<string | null | undefined>;
  // search form ref, dùng để set lại search form values từ url query
  searchFormRef?: React.MutableRefObject<FormInstance | undefined>;
  // 1 số request GET có các param trong path chứ không phải trong query
  pathParams?: Record<string, string>;
};

export type ApiParser<T> = {
  loading: boolean;
  data: T | null | undefined;
  request: (
    params: Record<string, any>,
    sort: Record<string, SortOrder>,
  ) => Promise<T>;
  refresh: () => Promise<T>;
  fetchOnly: (extraParams?: any) => Promise<T>;
};

const checkDate = (value?: string): boolean => {
  const formatSupports = [
    'YYYY-MM-DDTHH:mm:ss.SSSZ',
    moment.HTML5_FMT.DATETIME_LOCAL_MS,
    moment.HTML5_FMT.DATETIME_LOCAL_SECONDS,
    moment.HTML5_FMT.DATE,
  ];
  return formatSupports.some((f) => moment(value, f, true).isValid());
};

const defaultFormatDateWithTime = (value: Moment) =>
  value.format('YYYY-MM-DDThh:mm:ss');
type RequestWithBody<V, T> = (body: BaseApiRequestParam & V) => Promise<T>;
type RequestWithPathParams<V, T> = (
  pathParams: Record<string, string>,
  body: BaseApiRequestParam & V,
) => Promise<T>;

export const useApiParser = <T, V = T>(
  request: RequestWithBody<V, T> | RequestWithPathParams<V, T>,
  options?: UseApiParserOption<V>,
): ApiParser<T> => {
  const [data, setData] = useState<T | null | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  const [param, setParam] = useState<any>();
  const formatDateWithTime =
    options?.formatDateWithTime ?? defaultFormatDateWithTime;
  const message = useMessage();
  const { query, setQuery } = useApiParserQuery();

  const doRequest = async (requestParam: any) => {
    setLoading(true);

    try {
      const res = options?.pathParams
        ? await (request as RequestWithPathParams<V, T>)(options.pathParams, {
            ...requestParam,
            filter: { ...requestParam.filter, ...options?.additionalFilter },
          })
        : await (request as RequestWithBody<V, T>)({
            ...requestParam,
            filter: { ...requestParam.filter, ...options?.additionalFilter },
          });

      setData(res);

      return res;
    } finally {
      setLoading(false);
    }
  };

  const callApi = async (
    params: Record<string, any>,
    sort: Record<string, SortOrder>,
  ): Promise<T> => {
    const sorts: string[] = [];
    Object.keys(sort).forEach((k) => {
      if (k) {
        sorts.push(sort[k] === 'ascend' ? `${k}.asc` : `${k}.desc`);
      }
    });
    const sortQuery = sorts.length > 0 ? sorts.join(',') : undefined;

    const filterQuery = omit(params, ['current', 'pageSize']);
    const urlFilterQuery = { ...filterQuery };

    if (filterQuery) {
      Object.keys(filterQuery).forEach((k) => {
        const value = filterQuery[k];

        if (typeof value === 'string' && checkDate(value)) {
          filterQuery[k] = {
            between: [
              formatDateWithTime(moment(value).startOf('day')),
              formatDateWithTime(moment(value).endOf('day')),
            ],
          };
        } else if (
          typeof value === 'string' &&
          value.length > 0 &&
          options?.fieldsUsingLike?.includes(k)
        ) {
          filterQuery[k] = {
            iLike: `%${value}%`,
          };
        } else if (
          Array.isArray(value) &&
          value.length === 2 &&
          typeof value[0] === 'string' &&
          checkDate(value[0]) &&
          checkDate(value[1])
        ) {
          filterQuery[k] = {
            between: [
              formatDateWithTime(moment(value[0]).startOf('day')),
              formatDateWithTime(moment(value[1]).endOf('day')),
            ],
          };
        } else if (Array.isArray(value) && value.length > 0) {
          filterQuery[k] = {
            overlap: value, // && [1, 2] (PG array overlap operator)
          };
        } else if (value.length === 0) {
          delete filterQuery[k];
        }
      });
    }

    const additionRequestParams =
      (typeof options?.additionRequestParams === 'function'
        ? options?.additionRequestParams(filterQuery)
        : options?.additionRequestParams) ?? {};

    if (typeof options?.validateRequestParams === 'function') {
      const error = await options?.validateRequestParams({
        ...filterQuery,
        ...additionRequestParams,
      });

      if (error) {
        message.warning(error);
        throw new Error(error);
      }
    }

    const requestParam = {
      current: params.current,
      pageSize: params.pageSize,
      filter: filterQuery,
      sort: sortQuery,
      ...additionRequestParams,
    };
    setQuery(urlFilterQuery);
    setParam(requestParam);
    return await doRequest(requestParam);
  };

  useEffect(() => {
    options?.searchFormRef?.current?.setFieldsValue(query);
    options?.searchFormRef?.current?.submit();
  }, [options?.searchFormRef, JSON.stringify(query)]);

  return {
    loading,
    data,
    request: callApi,
    refresh: () => doRequest(param),
    fetchOnly: (extraParams?: any) => doRequest({ ...param, ...extraParams }),
  };
};
